import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import plusIcon from "@assets/facilityNetwork/lf-plus.svg";
import FacilityNetworkFigmaIcon from "./facilityNetwork/FacilityNetworkFigmaIcon";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFacilityLayoutData } from "@context";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  buildTopLevelFacilityOptions,
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
  SELECTED_FACILITY_UPDATED_EVENT,
  type FacilityHierarchyInput,
} from "@utils/facilityUtils";
import { isHoldingCompanyNetwork } from "@utils/networkFacilityRows";
import { resolveFacilityNetworkView, countDirectChildren } from "@utils/facilityNetworkView";
import { getActingParentFacilityId, getNetworkLinkListFacilityId } from "./facilityNetwork/facilityNetworkHierarchyUtils";
import {
  collectLinkedMembers,
  compositionCountsFromRequestLists,
  getImmediateParent,
  normalizeFacilityNetworkTab,
  resolveFacilityNetworkChrome,
} from "./facilityNetwork/facilityNetworkOverviewUtils";
import FacilityNetworkFacilityHeader from "./facilityNetwork/FacilityNetworkFacilityHeader";
import {
  getMyVerticalContext,
  getPreviewVertical,
  type VerticalContextResponse,
} from "@services/verticals";
import FacilityNetworkTabBar, {
  type FacilityNetworkTabId,
} from "./facilityNetwork/FacilityNetworkTabBar";
import FacilityNetworkTitleRow from "./facilityNetwork/FacilityNetworkTitleRow";
import FacilityNetworkParentOverviewTab from "./facilityNetwork/FacilityNetworkParentOverviewTab";
import FacilityNetworkChildView from "./facilityNetwork/FacilityNetworkChildView";
import FacilityNetworkRequestsInbox from "./facilityNetwork/FacilityNetworkRequestsInbox";
import FacilityNetworkChildrenTab from "./facilityNetwork/FacilityNetworkChildrenTab";
import FacilityNetworkChildDashboardView from "./facilityNetwork/FacilityNetworkChildDashboardView";
import FacilityNetworkRhCDashboardView, {
  isRhCTypeDashboard,
} from "./facilityNetwork/FacilityNetworkRhCDashboardView";
import FacilityNetworkCaHDashboardView from "./facilityNetwork/FacilityNetworkCaHDashboardView";
import { isCaHTypeDashboard } from "./facilityNetwork/facilityNetworkCaHDashboardUtils";
import FacilityNetworkFqhcDashboardView from "./facilityNetwork/FacilityNetworkFqhcDashboardView";
import { isFqhcTypeDashboard } from "./facilityNetwork/facilityNetworkFqhcDashboardUtils";
import FacilityNetworkSnfDashboardView from "./facilityNetwork/FacilityNetworkSnfDashboardView";
import { isSnfTypeDashboard } from "./facilityNetwork/facilityNetworkSnfDashboardUtils";
import { linkedFacilitiesSubtitle } from "./facilityNetwork/facilityNetworkChildrenUtils";
import FacilityNetworkPageShell from "./facilityNetwork/FacilityNetworkPageShell";
import FacilityNetworkParentLinkRequestBanner from "./facilityNetwork/FacilityNetworkParentLinkRequestBanner";
import FacilityNetworkUnlinkRequestBanner from "./facilityNetwork/FacilityNetworkUnlinkRequestBanner";
import FacilityNetworkConditionalPendingBanner from "./facilityNetwork/FacilityNetworkConditionalPendingBanner";
import FacilityNetworkConditionalPendingDialog from "./facilityNetwork/FacilityNetworkConditionalPendingDialog";
import ParentLinkRequestDialog from "./facilityNetwork/ParentLinkRequestDialog";
import UnlinkRequestDialog from "./facilityNetwork/UnlinkRequestDialog";
import { mapIncomingLinkRequestToBanner } from "./facilityNetwork/parentLinkRequestUtils";
import {
  mapReceivedUnlinkRequestToBanner,
  unlinkAcceptedSuccessMessage,
} from "./facilityNetwork/facilityNetworkUnlinkRequestUtils";
import { mapOutgoingToConditionalPending } from "./facilityNetwork/conditionalPendingUtils";
import type { ParentLinkRequestBannerData } from "./facilityNetwork/parentLinkRequestUtils";
import type { UnlinkRequestBannerData } from "./facilityNetwork/facilityNetworkUnlinkRequestUtils";
import type { ConditionalPendingRequestData } from "./facilityNetwork/conditionalPendingUtils";
import { FN } from "./facilityNetwork/facilityNetworkTheme";
import {
  acceptIncomingChildLinkRequest,
  cancelChildLinkRequest,
  declineIncomingChildLinkRequest,
  listChildLinkRequests,
  listIncomingChildLinkRequests,
  type IncomingChildLinkRequestItem,
} from "@services/facilityNetworkLink";
import { isAcceptedLinkedStatus } from "./facilityNetwork/facilityNetworkChildrenUtils";
import {
  approveUnlinkRequest,
  listUnlinkRequests,
  rejectUnlinkRequest,
} from "@services/facilityNetworkUnlink";
import { isFacilityNetworkNotFoundMessage } from "@services/facilityNetworkListFetch";
import { countOpenNetworkInboxItems } from "./facilityNetwork/facilityNetworkInboxUtils";
import { toast } from "react-toastify";
import {
  consumeNetworkFocusFacilityId,
  peekNetworkFocusFacilityId,
} from "@services/notifications";

type NetworkFacility = MyFacilityListRow & {
  facility_id?: number;
};

function myFacilityRowToNetworkFacility(row: MyFacilityListRow): NetworkFacility {
  return {
    ...row,
    id: row.facility_id ?? row.id,
  };
}

/** Linked children belong to the child org; a parent must not select them here. */
function findFacilityById(
  facilities: NetworkFacility[],
  facilityId: number,
): NetworkFacility | undefined {
  return facilities.find((f) => getFacilityHierarchyId(f) === facilityId);
}

function incomingParentToFacility(
  request: IncomingChildLinkRequestItem,
): FacilityHierarchyInput {
  return {
    id: request.parent_facility_id,
    facility_id: request.parent_facility_id,
    facility_name: request.parent_facility_name,
    npi_number: request.parent_npi ?? null,
    medicare_ccn: request.parent_ccn ?? null,
    facility_type: request.parent_facility_type ?? null,
    city: request.parent_city ?? null,
    state: request.parent_state ?? null,
    full_address: request.parent_location ?? null,
    facility_group_id: request.parent_facility_group_id ?? null,
  };
}

function findAcceptedParentRequest(
  requests: IncomingChildLinkRequestItem[],
  parentGroupId: string,
): IncomingChildLinkRequestItem | undefined {
  const accepted = requests.filter((row) => isAcceptedLinkedStatus(row.status || ""));
  const wanted = parentGroupId.trim().toUpperCase();
  return (
    accepted.find(
      (row) => (row.parent_facility_group_id || "").trim().toUpperCase() === wanted,
    ) ?? accepted[0]
  );
}

function storeSelectedFacility(facility: NetworkFacility | undefined): void {
  if (typeof window === "undefined") return;
  if (!facility) {
    sessionStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
    notifySelectedFacilitySessionUpdated();
    return;
  }
  sessionStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, JSON.stringify(facility));
  notifySelectedFacilitySessionUpdated();
}

export default function FacilityNetworkPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const routeState = location.state as {
    selectedFacility?: NetworkFacility;
    selectedFacilityId?: number;
    networkTab?: FacilityNetworkTabId;
    refreshFacilities?: boolean;
  } | null;
  const queryFacilityId = useMemo(() => {
    const raw = searchParams.get("facilityId");
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [searchParams]);
  const queryTab = useMemo(() => {
    const raw = searchParams.get("tab");
    if (!raw) return null;
    return normalizeFacilityNetworkTab(raw as FacilityNetworkTabId);
  }, [searchParams]);
  const { myFacilities, loading: layoutLoading, refreshMyFacilities } = useFacilityLayoutData();
  const prevUnlinkPendingRef = useRef<number | null>(null);
  const appliedFocusRef = useRef<number | null>(null);
  /** User picked a facility from dropdown — stop re-applying notification deep-link. */
  const dismissFocusRef = useRef(false);
  const [previewVerticalSlug, setPreviewVerticalSlug] = useState<string | null>(
    () => getPreviewVertical(),
  );
  const [verticalContext, setVerticalContext] =
    useState<VerticalContextResponse | null>(null);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | "">("");
  const [activeTab, setActiveTab] = useState<FacilityNetworkTabId>(
    queryTab ?? routeState?.networkTab ?? "overview",
  );
  const [linkRequestCount, setLinkRequestCount] = useState(0);
  const [requestListsReloadKey, setRequestListsReloadKey] = useState(0);
  const [compositionExtras, setCompositionExtras] = useState({
    declinedCount: 0,
    inviteCount: 0,
  });
  const [linkedTypeFilter, setLinkedTypeFilter] = useState("all");
  const [incomingLinkRequestError, setIncomingLinkRequestError] = useState<string | null>(null);
  const [parentLinkBanner, setParentLinkBanner] =
    useState<ParentLinkRequestBannerData | null>(null);
  const [unlinkBanner, setUnlinkBanner] = useState<UnlinkRequestBannerData | null>(null);
  const [conditionalPendingBanner, setConditionalPendingBanner] =
    useState<ConditionalPendingRequestData | null>(null);
  const [linkReviewOpen, setLinkReviewOpen] = useState(false);
  const [unlinkReviewOpen, setUnlinkReviewOpen] = useState(false);
  const [conditionalPendingOpen, setConditionalPendingOpen] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  /** Linked-child individual dashboard (separate from My Facility tabs). */
  const [viewingChildDashboard, setViewingChildDashboard] =
    useState<MyFacilityListRow | null>(null);
  /** Type-detail dashboard (e.g. RHC Figma 9120:91639). */
  const [viewingTypeDashboard, setViewingTypeDashboard] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!routeState?.refreshFacilities) return;
    void refreshMyFacilities({ bypassCache: true });
  }, [routeState?.refreshFacilities, refreshMyFacilities]);

  useEffect(() => {
    let cancelled = false;
    getMyVerticalContext()
      .then((ctx) => {
        if (!cancelled) setVerticalContext(ctx);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ slug: string | null }>).detail;
      setPreviewVerticalSlug(detail?.slug ?? null);
    };
    window.addEventListener(
      "unicomply:preview-vertical-changed",
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        "unicomply:preview-vertical-changed",
        handler as EventListener,
      );
    };
  }, []);

  const activeVertical = useMemo<string>(() => {
    if (previewVerticalSlug) return previewVerticalSlug;
    return (
      verticalContext?.effective_vertical ||
      verticalContext?.user_vertical ||
      "healthcare"
    );
  }, [previewVerticalSlug, verticalContext]);

  const visibleFacilities = useMemo(
    () =>
      myFacilities
        .filter(
          (row) =>
            (row.vertical || "healthcare") === activeVertical &&
            !isHoldingCompanyNetwork(row),
        )
        .map(myFacilityRowToNetworkFacility),
    [myFacilities, activeVertical],
  );

  const groupedFacilityOptions = useMemo(
    () => buildTopLevelFacilityOptions(visibleFacilities),
    [visibleFacilities],
  );

  const resolveFocusFacilityId = useCallback((): number | null => {
    if (dismissFocusRef.current) return null;
    if (queryFacilityId != null) return queryFacilityId;
    if (routeState?.selectedFacilityId != null) {
      const id = Number(routeState.selectedFacilityId);
      if (Number.isFinite(id) && id > 0) return id;
    }
    const fromSelected = getFacilityHierarchyId(routeState?.selectedFacility ?? {});
    if (fromSelected != null && fromSelected > 0) return fromSelected;
    return peekNetworkFocusFacilityId();
  }, [
    queryFacilityId,
    routeState?.selectedFacility,
    routeState?.selectedFacilityId,
  ]);

  const clearNotificationFocus = useCallback(() => {
    dismissFocusRef.current = true;
    appliedFocusRef.current = null;
    consumeNetworkFocusFacilityId();
    const hasQuery = searchParams.has("facilityId") || searchParams.has("tab");
    const hasRouteFocus =
      routeState?.selectedFacilityId != null ||
      routeState?.selectedFacility != null;
    if (!hasQuery && !hasRouteFocus) return;

    const next = new URLSearchParams(searchParams);
    next.delete("facilityId");
    next.delete("tab");
    const nextSearch = next.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      {
        replace: true,
        state: {
          ...(routeState || {}),
          currentView: "facility-network",
          selectedFacilityId: undefined,
          selectedFacility: undefined,
          networkTab: undefined,
        },
      },
    );
  }, [searchParams, routeState, navigate, location.pathname]);

  // New deep-link in URL re-enables focus apply (e.g. another notification click).
  useEffect(() => {
    if (queryFacilityId == null) return;
    if (appliedFocusRef.current === queryFacilityId) return;
    dismissFocusRef.current = false;
  }, [queryFacilityId]);

  const resolveInitialFacilityId = useCallback((): number | "" => {
    const focusId = resolveFocusFacilityId();
    if (focusId != null) {
      const inVisible = findFacilityById(visibleFacilities, focusId);
      const inAll =
        inVisible ??
        findFacilityById(
          myFacilities
            .filter((row) => !isHoldingCompanyNetwork(row))
            .map(myFacilityRowToNetworkFacility),
          focusId,
        );
      if (inAll) return focusId;
    }
    const stored = getSelectedFacilityFromSession<NetworkFacility>();
    if (stored) {
      const storedId = getFacilityHierarchyId(stored);
      if (storedId != null && findFacilityById(visibleFacilities, storedId)) {
        return storedId;
      }
    }
    const first = visibleFacilities[0];
    return first ? (getFacilityHierarchyId(first) ?? "") : "";
  }, [resolveFocusFacilityId, visibleFacilities, myFacilities]);

  // Deep-link / notification: apply once per focus id. Do not fight dropdown changes.
  useEffect(() => {
    if (dismissFocusRef.current) return;
    const focusId = resolveFocusFacilityId();
    if (focusId == null) return;
    if (!myFacilities.length) return;
    if (appliedFocusRef.current === focusId) return;

    const verticalMatched = findFacilityById(visibleFacilities, focusId);
    const anyMatched =
      verticalMatched ??
      findFacilityById(
        myFacilities
          .filter((row) => !isHoldingCompanyNetwork(row))
          .map(myFacilityRowToNetworkFacility),
        focusId,
      );
    if (!anyMatched) return;

    appliedFocusRef.current = focusId;
    setSelectedFacilityId(focusId);
    storeSelectedFacility(anyMatched);
    consumeNetworkFocusFacilityId();
    const tab = queryTab ?? routeState?.networkTab;
    if (tab) {
      setActiveTab(normalizeFacilityNetworkTab(tab));
    }
  }, [
    resolveFocusFacilityId,
    visibleFacilities,
    myFacilities,
    queryTab,
    routeState?.networkTab,
  ]);

  useEffect(() => {
    if (!visibleFacilities.length && !myFacilities.length) {
      setSelectedFacilityId("");
      storeSelectedFacility(undefined);
      return;
    }
    const focusId = resolveFocusFacilityId();
    setSelectedFacilityId((current) => {
      if (
        !dismissFocusRef.current &&
        focusId != null &&
        appliedFocusRef.current !== focusId
      ) {
        const inVisible = findFacilityById(visibleFacilities, focusId);
        const inAll =
          inVisible ??
          findFacilityById(
            myFacilities
              .filter((row) => !isHoldingCompanyNetwork(row))
              .map(myFacilityRowToNetworkFacility),
            focusId,
          );
        if (inAll) {
          appliedFocusRef.current = focusId;
          storeSelectedFacility(inAll);
          return focusId;
        }
      }
      if (
        current !== "" &&
        findFacilityById(visibleFacilities, Number(current))
      ) {
        return current;
      }
      const initialId = resolveInitialFacilityId();
      if (initialId !== "") {
        const facility =
          findFacilityById(visibleFacilities, Number(initialId)) ??
          findFacilityById(
            myFacilities
              .filter((row) => !isHoldingCompanyNetwork(row))
              .map(myFacilityRowToNetworkFacility),
            Number(initialId),
          );
        if (facility) storeSelectedFacility(facility);
      }
      return initialId;
    });
  }, [
    visibleFacilities,
    myFacilities,
    resolveInitialFacilityId,
    resolveFocusFacilityId,
  ]);

  useEffect(() => {
    const onSelectedUpdated = () => {
      if (!dismissFocusRef.current) {
        const focusId = resolveFocusFacilityId();
        if (focusId != null && appliedFocusRef.current !== focusId) {
          const match =
            findFacilityById(visibleFacilities, focusId) ??
            findFacilityById(
              myFacilities
                .filter((row) => !isHoldingCompanyNetwork(row))
                .map(myFacilityRowToNetworkFacility),
              focusId,
            );
          if (match) {
            appliedFocusRef.current = focusId;
            setSelectedFacilityId(focusId);
            return;
          }
        }
      }
      const stored = getSelectedFacilityFromSession<NetworkFacility>();
      if (!stored) return;
      const storedId = getFacilityHierarchyId(stored);
      if (storedId == null) return;
      if (findFacilityById(visibleFacilities, storedId)) {
        setSelectedFacilityId(storedId);
      }
    };
    window.addEventListener(SELECTED_FACILITY_UPDATED_EVENT, onSelectedUpdated);
    return () => {
      window.removeEventListener(SELECTED_FACILITY_UPDATED_EVENT, onSelectedUpdated);
    };
  }, [visibleFacilities, myFacilities, resolveFocusFacilityId]);

  const selectedFacility = useMemo(() => {
    if (selectedFacilityId === "") return undefined;
    const id = Number(selectedFacilityId);
    return (
      findFacilityById(visibleFacilities, id) ??
      findFacilityById(
        myFacilities
          .filter((row) => !isHoldingCompanyNetwork(row))
          .map(myFacilityRowToNetworkFacility),
        id,
      )
    );
  }, [visibleFacilities, myFacilities, selectedFacilityId]);

  const networkView = useMemo(() => {
    if (!selectedFacility) return null;
    return resolveFacilityNetworkView(selectedFacility, visibleFacilities);
  }, [selectedFacility, visibleFacilities]);

  const actingParentFacilityId = useMemo(
    () => getActingParentFacilityId(networkView),
    [networkView],
  );
  const listFacilityId = useMemo(
    () => getNetworkLinkListFacilityId(networkView),
    [networkView],
  );

  const chrome = useMemo(
    () => resolveFacilityNetworkChrome(networkView),
    [networkView],
  );
  const isChildChrome = chrome === "child";
  const canAddChildFacilities = networkView != null && actingParentFacilityId != null;

  const [externalParent, setExternalParent] = useState<FacilityHierarchyInput | null>(null);
  const needsExternalParent =
    isChildChrome && networkView != null && getImmediateParent(networkView) == null;
  const childFacilityIdForParent =
    needsExternalParent && networkView ? getFacilityHierarchyId(networkView.selected) : null;
  const childParentGroupId = networkView?.selected.parent_facility_group_id?.trim() || "";

  useEffect(() => {
    if (childFacilityIdForParent == null) {
      setExternalParent(null);
      return;
    }
    let cancelled = false;
    listIncomingChildLinkRequests(childFacilityIdForParent)
      .then((rows) => {
        if (cancelled) return;
        const accepted = findAcceptedParentRequest(rows, childParentGroupId);
        setExternalParent(accepted ? incomingParentToFacility(accepted) : null);
      })
      .catch(() => {
        if (!cancelled) setExternalParent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [childFacilityIdForParent, childParentGroupId, requestListsReloadKey]);

  const childNetworkView = useMemo(() => {
    if (!networkView || !needsExternalParent || !externalParent) return networkView;
    return { ...networkView, ancestors: [externalParent] };
  }, [networkView, needsExternalParent, externalParent]);

  useEffect(() => {
    if (routeState?.networkTab) {
      setActiveTab(normalizeFacilityNetworkTab(routeState.networkTab));
      return;
    }
    setActiveTab((current) => normalizeFacilityNetworkTab(current));
  }, [routeState?.networkTab]);

  useEffect(() => {
    if (isChildChrome && activeTab === "children") {
      setActiveTab("overview");
    }
  }, [isChildChrome, activeTab]);

  const clearNetworkRequestBanners = useCallback(() => {
    setParentLinkBanner(null);
    setUnlinkBanner(null);
    setConditionalPendingBanner(null);
    setLinkReviewOpen(false);
    setUnlinkReviewOpen(false);
    setConditionalPendingOpen(false);
  }, []);

  useEffect(() => {
    const selectedId = selectedFacilityId === "" ? null : Number(selectedFacilityId);
    // Hide previous facility banners immediately — do not wait for new fetch
    clearNetworkRequestBanners();
    if (!listFacilityId && selectedId == null) {
      setLinkRequestCount(0);
      setCompositionExtras({ declinedCount: 0, inviteCount: 0 });
      setIncomingLinkRequestError(null);
      prevUnlinkPendingRef.current = null;
      return;
    }

    let cancelled = false;

    const loadRequestLists = () => {
      const sentPromise =
        listFacilityId && !isChildChrome
          ? listChildLinkRequests(listFacilityId)
          : Promise.resolve([]);
      const receivedPromise =
        selectedId != null
          ? listIncomingChildLinkRequests(selectedId)
          : Promise.resolve([]);
      const initiatedUnlinkPromise =
        selectedId != null ? listUnlinkRequests(selectedId, "initiated") : Promise.resolve([]);
      const receivedUnlinkPromise =
        selectedId != null ? listUnlinkRequests(selectedId, "received") : Promise.resolve([]);
      return Promise.all([sentPromise, receivedPromise, initiatedUnlinkPromise, receivedUnlinkPromise])
        .then(([sent, received, initiatedUnlink, receivedUnlink]) => {
          if (cancelled) return;
          const pendingUnlink = [...initiatedUnlink, ...receivedUnlink].filter(
            (item) => item.status === "pending",
          ).length;
          const prevPending = prevUnlinkPendingRef.current;
          if (prevPending != null && pendingUnlink < prevPending) {
            void refreshMyFacilities({ bypassCache: true });
          }
          prevUnlinkPendingRef.current = pendingUnlink;
          setLinkRequestCount(
            countOpenNetworkInboxItems({
              sent,
              received,
              initiatedUnlink,
              receivedUnlink,
              linked: [],
              parentFacilityId: listFacilityId && !isChildChrome ? listFacilityId : null,
              selectedFacilityId: selectedId,
            }),
          );
          const composition = compositionCountsFromRequestLists({
            linkedCount: 0,
            sent,
            received,
            openInboxCount: 0,
          });
          setCompositionExtras({
            declinedCount: composition.declinedCount,
            inviteCount: composition.inviteCount,
          });
          // AB#10946 — A receives: first pending incoming parent link (not accepted/declined)
          const pendingIncoming =
            received.find(
              (row) =>
                (row.status === "link_pending" || row.status === "pending") &&
                !row.coordination_unlink_id,
            ) ?? null;
          setParentLinkBanner(
            pendingIncoming ? mapIncomingLinkRequestToBanner(pendingIncoming) : null,
          );
          // AB#10945 — B notified: first pending received unlink (never name P_new)
          const pendingReceivedUnlink = receivedUnlink.find((row) => row.status === "pending");
          setUnlinkBanner(
            pendingReceivedUnlink
              ? mapReceivedUnlinkRequestToBanner(pendingReceivedUnlink)
              : null,
          );
          // Figma 9227:111011 — C waiting: outgoing coordination_mode pending
          const pendingCoordination = sent.find(
            (row) =>
              Boolean(row.coordination_mode) &&
              (row.status === "link_pending" || row.status === "pending"),
          );
          setConditionalPendingBanner(
            pendingCoordination
              ? mapOutgoingToConditionalPending(pendingCoordination)
              : null,
          );
          setIncomingLinkRequestError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setLinkRequestCount(0);
          setParentLinkBanner(null);
          setUnlinkBanner(null);
          setConditionalPendingBanner(null);
          prevUnlinkPendingRef.current = null;
          const message =
            err instanceof Error ? err.message : "Failed to load network requests.";
          if (isFacilityNetworkNotFoundMessage(message)) {
            setIncomingLinkRequestError(null);
            return;
          }
          setIncomingLinkRequestError(message);
        });
    };

    void loadRequestLists();

    const shouldPoll = activeTab === "children" || activeTab === "requests" || isChildChrome;
    const pollId = shouldPoll
      ? window.setInterval(() => {
          void loadRequestLists();
        }, 20000)
      : undefined;
    const onFocus = () => {
      if (shouldPoll) void loadRequestLists();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (pollId != null) window.clearInterval(pollId);
      window.removeEventListener("focus", onFocus);
    };
  }, [
    listFacilityId,
    selectedFacilityId,
    activeTab,
    isChildChrome,
    refreshMyFacilities,
    requestListsReloadKey,
    clearNetworkRequestBanners,
  ]);

  const refreshNetworkRequestLists = useCallback(() => {
    setRequestListsReloadKey((key) => key + 1);
  }, []);

  const handleNetworkMutated = useCallback(() => {
    refreshNetworkRequestLists();
    void refreshMyFacilities({ bypassCache: true });
  }, [refreshMyFacilities, refreshNetworkRequestLists]);

  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    const prevTab = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (
      (activeTab === "children" || activeTab === "hierarchy") &&
      prevTab !== activeTab
    ) {
      void refreshMyFacilities({ bypassCache: true });
    }
  }, [activeTab, refreshMyFacilities]);

  const childCount = useMemo(() => {
    if (!networkView) return 0;
    return countDirectChildren(networkView);
  }, [networkView]);

  const linkedCount = useMemo(() => {
    if (!networkView) return 0;
    return collectLinkedMembers(networkView).length;
  }, [networkView]);

  const overviewComposition = useMemo(
    () => ({
      linkedCount,
      pendingCount: linkRequestCount,
      declinedCount: compositionExtras.declinedCount,
      inviteCount: compositionExtras.inviteCount,
    }),
    [linkedCount, linkRequestCount, compositionExtras],
  );

  const navigateToFacilityDashboard = (facility: FacilityHierarchyInput) => {
    const facilityId = getFacilityHierarchyId(facility);
    const match =
      facilityId != null ? findFacilityById(visibleFacilities, facilityId) : undefined;
    if (!match) return;
    storeSelectedFacility(match);
    navigate("/dashboard/facilities", {
      state: {
        currentView: "MyFacility",
        selectedFacility: match,
        returnToNetworkOverview: true,
      },
    });
  };

  const tabs = useMemo(() => {
    const requestsTab = {
      id: "requests" as const,
      label: "Requests",
      count: linkRequestCount,
    };
    if (isChildChrome) {
      return [{ id: "overview" as const, label: "Network" }, requestsTab];
    }
    return [
      { id: "overview" as const, label: "Overview" },
      { id: "children" as const, label: "Linked Facilities", count: childCount },
      requestsTab,
    ];
  }, [isChildChrome, childCount, linkRequestCount]);

  const handleFacilityChange = (event: SelectChangeEvent<number | "">) => {
    const raw = event.target.value;
    setViewingChildDashboard(null);
    setViewingTypeDashboard(null);
    clearNetworkRequestBanners();
    setActiveTab("overview");
    clearNotificationFocus();
    if (raw === "") {
      setSelectedFacilityId("");
      storeSelectedFacility(undefined);
      return;
    }
    const facilityId = typeof raw === "number" ? raw : Number(raw);
    const facility =
      findFacilityById(visibleFacilities, facilityId) ??
      findFacilityById(
        myFacilities
          .filter((row) => !isHoldingCompanyNetwork(row))
          .map(myFacilityRowToNetworkFacility),
        facilityId,
      );
    setSelectedFacilityId(facilityId);
    storeSelectedFacility(facility);
  };

  const handleNetworkTabChange = (tab: FacilityNetworkTabId) => {
    setViewingChildDashboard(null);
    setViewingTypeDashboard(null);
    setActiveTab(tab);
  };

  const navigateToAddChildFacilities = () => {
    navigate("/dashboard/facilities", {
      state: {
        currentView: "facility-network-add-children",
        selectedFacility,
      },
    });
  };

  const navigateToParentDetails = () => {
    if (!networkView) return;
    const parent = getImmediateParent(networkView);
    if (!parent) return;
    const parentId = getFacilityHierarchyId(parent);
    const facility =
      parentId != null ? findFacilityById(visibleFacilities, parentId) : undefined;
    if (!facility) return;
    storeSelectedFacility(facility);
    navigate("/dashboard/facilities", {
      state: {
        currentView: "MyFacility",
        selectedFacility: facility,
      },
    });
  };

  const addChildButton = (
    <Button
      variant="contained"
      startIcon={<FacilityNetworkFigmaIcon src={plusIcon} size={16} />}
      onClick={navigateToAddChildFacilities}
      sx={{
        textTransform: "none",
        borderRadius: "8px",
        px: 3,
        py: 1.5,
        fontSize: 14,
        fontWeight: 600,
        fontFamily: FN.fontFamily,
        color: "#e6fafc",
        background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)",
        "& .MuiButton-startIcon": { mr: 1 },
        "&:hover": {
          background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
        },
      }}
    >
      Add child facilities
    </Button>
  );

  if (layoutLoading) {
    return (
      <FacilityNetworkPageShell>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            width: "100%",
            minHeight: 240,
          }}
        >
          <CircularProgress size={28} sx={{ color: FN.primary }} />
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: FN.figmaAccent,
              lineHeight: "20px",
            }}
          >
            Loading My Network…
          </Typography>
        </Box>
      </FacilityNetworkPageShell>
    );
  }

  return (
    <FacilityNetworkPageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          width: "100%",
          flex: 1,
        }}
      >
        <FacilityNetworkFacilityHeader
          selectedFacilityId={selectedFacilityId}
          selectedFacility={selectedFacility}
          groupedFacilityOptions={groupedFacilityOptions}
          onFacilityChange={handleFacilityChange}
        />

        <Box sx={{ mt: FN.headerToTabs }}>
          {viewingChildDashboard || viewingTypeDashboard ? null : (
            <FacilityNetworkTabBar
              tabs={tabs}
              value={isChildChrome && activeTab === "children" ? "overview" : activeTab}
              onChange={handleNetworkTabChange}
              density="figma"
            />
          )}
          {!isChildChrome &&
          activeTab === "children" &&
          !viewingChildDashboard &&
          !viewingTypeDashboard ? (
            <FacilityNetworkTitleRow
              title="Linked facilities"
              badge={linkedFacilitiesSubtitle(childCount)}
              icon={null}
              action={canAddChildFacilities ? addChildButton : undefined}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            pt: FN.contentAreaTopPad,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {viewingChildDashboard ? (
            <FacilityNetworkChildDashboardView
              facility={viewingChildDashboard}
              onBack={() => {
                setViewingChildDashboard(null);
                setActiveTab("children");
              }}
            />
          ) : viewingTypeDashboard &&
            isRhCTypeDashboard(viewingTypeDashboard) &&
            networkView ? (
            <FacilityNetworkRhCDashboardView
              members={networkView.members}
              selectedFacilityId={
                selectedFacilityId === "" ? null : Number(selectedFacilityId)
              }
              networkId={selectedFacility?.network_id ?? null}
              onBack={() => {
                setViewingTypeDashboard(null);
                setActiveTab("overview");
              }}
              onOpenFacility={(facility) => {
                setViewingTypeDashboard(null);
                setViewingChildDashboard(facility);
              }}
            />
          ) : viewingTypeDashboard &&
            isSnfTypeDashboard(viewingTypeDashboard) &&
            networkView ? (
            <FacilityNetworkSnfDashboardView
              members={networkView.members}
              selectedFacilityId={
                selectedFacilityId === "" ? null : Number(selectedFacilityId)
              }
              networkId={selectedFacility?.network_id ?? null}
              onBack={() => {
                setViewingTypeDashboard(null);
                setActiveTab("overview");
              }}
              onOpenFacility={(facility) => {
                setViewingTypeDashboard(null);
                setViewingChildDashboard(facility);
              }}
            />
          ) : viewingTypeDashboard &&
            isCaHTypeDashboard(viewingTypeDashboard) &&
            networkView ? (
            <FacilityNetworkCaHDashboardView
              members={networkView.members}
              selectedFacilityId={
                selectedFacilityId === "" ? null : Number(selectedFacilityId)
              }
              networkId={selectedFacility?.network_id ?? null}
              onBack={() => {
                setViewingTypeDashboard(null);
                setActiveTab("overview");
              }}
              onOpenFacility={(facility) => {
                setViewingTypeDashboard(null);
                setViewingChildDashboard(facility);
              }}
            />
          ) : viewingTypeDashboard &&
            isFqhcTypeDashboard(viewingTypeDashboard) &&
            networkView ? (
            <FacilityNetworkFqhcDashboardView
              members={networkView.members}
              selectedFacilityId={
                selectedFacilityId === "" ? null : Number(selectedFacilityId)
              }
              onBack={() => {
                setViewingTypeDashboard(null);
                setActiveTab("overview");
              }}
              onOpenFacility={(facility) => {
                setViewingTypeDashboard(null);
                setViewingChildDashboard(facility);
              }}
            />
          ) : (
            <>
          {parentLinkBanner && activeTab === "overview" ? (
            <FacilityNetworkParentLinkRequestBanner
              request={parentLinkBanner}
              onAction={() => setLinkReviewOpen(true)}
            />
          ) : null}
          {unlinkBanner && activeTab === "overview" ? (
            <FacilityNetworkUnlinkRequestBanner
              request={unlinkBanner}
              severity="error"
              onAction={() => setUnlinkReviewOpen(true)}
            />
          ) : null}
          {conditionalPendingBanner && activeTab === "overview" && !isChildChrome ? (
            <FacilityNetworkConditionalPendingBanner
              request={conditionalPendingBanner}
              onAction={() => setConditionalPendingOpen(true)}
            />
          ) : null}
          {!visibleFacilities.length ? (
            <Alert severity="info">
              Add an active facility to view and manage your facility network.
            </Alert>
          ) : !selectedFacility || !networkView ? (
            <Alert severity="info">Select a facility to view its network.</Alert>
          ) : (
            <>
              {incomingLinkRequestError ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {incomingLinkRequestError}
                </Alert>
              ) : null}
              {isChildChrome && activeTab !== "requests" ? (
                <FacilityNetworkChildView
                  networkView={childNetworkView ?? networkView}
                  onViewParentDetails={navigateToParentDetails}
                  onNetworkMutated={() => {
                    setActiveTab("requests");
                    handleNetworkMutated();
                  }}
                />
              ) : null}
              {isChildChrome && activeTab === "requests" ? (
                <FacilityNetworkRequestsInbox
                  parentFacilityId={null}
                  selectedFacilityId={
                    selectedFacilityId === "" ? null : Number(selectedFacilityId)
                  }
                  onNetworkMutated={handleNetworkMutated}
                  onOpenCountChange={setLinkRequestCount}
                />
              ) : null}
              {!isChildChrome && activeTab === "overview" ? (
                <FacilityNetworkParentOverviewTab
                  networkView={networkView}
                  composition={overviewComposition}
                  onGoToRequests={() => setActiveTab("requests")}
                  onGoToLinkedFacilities={(typeFilter) => {
                    if (isRhCTypeDashboard(typeFilter)) {
                      setViewingChildDashboard(null);
                      setViewingTypeDashboard("RHC");
                      return;
                    }
                    if (isSnfTypeDashboard(typeFilter)) {
                      setViewingChildDashboard(null);
                      setViewingTypeDashboard("SNF");
                      return;
                    }
                    if (isCaHTypeDashboard(typeFilter)) {
                      setViewingChildDashboard(null);
                      setViewingTypeDashboard("CAH");
                      return;
                    }
                    if (isFqhcTypeDashboard(typeFilter)) {
                      setViewingChildDashboard(null);
                      setViewingTypeDashboard("FQHC");
                      return;
                    }
                    setLinkedTypeFilter(typeFilter || "all");
                    setActiveTab("children");
                  }}
                  onAddChildFacilities={
                    canAddChildFacilities ? navigateToAddChildFacilities : undefined
                  }
                  onOpenFacility={navigateToFacilityDashboard}
                  onViewChildDashboard={(facility) => {
                    setActiveTab("children");
                    setViewingChildDashboard(facility);
                  }}
                />
              ) : null}
              {!isChildChrome && activeTab === "children" ? (
                <FacilityNetworkChildrenTab
                  networkView={networkView}
                  initialTypeFilter={linkedTypeFilter}
                  onAddChildFacilities={
                    canAddChildFacilities ? navigateToAddChildFacilities : undefined
                  }
                  onViewChildDashboard={(facility) => {
                    setActiveTab("children");
                    setViewingChildDashboard(facility);
                  }}
                />
              ) : null}
              {!isChildChrome && activeTab === "requests" ? (
                <FacilityNetworkRequestsInbox
                  parentFacilityId={listFacilityId}
                  selectedFacilityId={
                    selectedFacilityId === "" ? null : Number(selectedFacilityId)
                  }
                  onNetworkMutated={handleNetworkMutated}
                  onOpenCountChange={setLinkRequestCount}
                />
              ) : null}
            </>
          )}
            </>
          )}
        </Box>
      </Box>

      <ParentLinkRequestDialog
        open={linkReviewOpen && Boolean(parentLinkBanner)}
        request={parentLinkBanner}
        submitting={reviewSubmitting}
        onClose={() => {
          if (!reviewSubmitting) setLinkReviewOpen(false);
        }}
        onAccept={async () => {
          if (!parentLinkBanner || selectedFacilityId === "") return;
          setReviewSubmitting(true);
          try {
            await acceptIncomingChildLinkRequest(
              Number(selectedFacilityId),
              parentLinkBanner.requestId,
            );
            toast.success(
              parentLinkBanner.coordinationMode
                ? parentLinkBanner.currentParentFacilityName
                  ? `Unlink request sent to ${parentLinkBanner.currentParentFacilityName}.`
                  : "Unlink request sent to your current parent."
                : "Link request accepted.",
            );
            setLinkReviewOpen(false);
            setParentLinkBanner(null);
            handleNetworkMutated();
          } catch (err) {
            const message =
              err instanceof Error ? err.message : "Failed to accept link request.";
            toast.error(message);
            if (/already been (declined|accepted|cancelled|resolved)/i.test(message)) {
              setLinkReviewOpen(false);
              setParentLinkBanner(null);
              handleNetworkMutated();
            }
          } finally {
            setReviewSubmitting(false);
          }
        }}
        onDecline={async (reason) => {
          if (!parentLinkBanner || selectedFacilityId === "") return;
          setReviewSubmitting(true);
          try {
            await declineIncomingChildLinkRequest(
              Number(selectedFacilityId),
              parentLinkBanner.requestId,
              reason,
            );
            toast.success("Link request declined.");
            setLinkReviewOpen(false);
            setParentLinkBanner(null);
            handleNetworkMutated();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to decline link request.");
          } finally {
            setReviewSubmitting(false);
          }
        }}
      />

      <UnlinkRequestDialog
        open={unlinkReviewOpen && Boolean(unlinkBanner)}
        request={unlinkBanner}
        submitting={reviewSubmitting}
        onClose={() => {
          if (!reviewSubmitting) setUnlinkReviewOpen(false);
        }}
        onAccept={async () => {
          if (!unlinkBanner || selectedFacilityId === "") return;
          setReviewSubmitting(true);
          try {
            await approveUnlinkRequest(Number(selectedFacilityId), unlinkBanner.requestId);
            toast.success(
              unlinkAcceptedSuccessMessage({
                initiatorRole: unlinkBanner.initiatorRole,
                counterpartyName: unlinkBanner.counterpartyFacilityName,
              }),
            );
            setUnlinkReviewOpen(false);
            setUnlinkBanner(null);
            handleNetworkMutated();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to approve unlink.");
          } finally {
            setReviewSubmitting(false);
          }
        }}
        onDecline={async (reason) => {
          if (!unlinkBanner || selectedFacilityId === "") return;
          setReviewSubmitting(true);
          try {
            await rejectUnlinkRequest(
              Number(selectedFacilityId),
              unlinkBanner.requestId,
              reason,
            );
            toast.success("Unlink request rejected.");
            setUnlinkReviewOpen(false);
            setUnlinkBanner(null);
            handleNetworkMutated();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to reject unlink.");
          } finally {
            setReviewSubmitting(false);
          }
        }}
      />
      <FacilityNetworkConditionalPendingDialog
        open={conditionalPendingOpen && Boolean(conditionalPendingBanner)}
        request={conditionalPendingBanner}
        submitting={reviewSubmitting}
        onClose={() => setConditionalPendingOpen(false)}
        onCancel={async () => {
          if (!conditionalPendingBanner || !listFacilityId) return;
          setReviewSubmitting(true);
          try {
            await cancelChildLinkRequest(
              listFacilityId,
              conditionalPendingBanner.requestId,
            );
            toast.success("Link request cancelled.");
            setConditionalPendingOpen(false);
            setConditionalPendingBanner(null);
            handleNetworkMutated();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : "Failed to cancel request.");
          } finally {
            setReviewSubmitting(false);
          }
        }}
      />
    </FacilityNetworkPageShell>
  );
}
